import * as React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Button, Label } from 'reactstrap';
import { PulseLoader } from 'react-spinners';
import { toastr } from 'react-redux-toastr';
import ReactGA from 'react-ga';
import { IReduxState } from '../../../reduxTypes';
import { ProvidersService } from '../../../share/services/providers';
import { IProvider, ProviderType } from '../../../share/types/provider';
import { SearchImageForm } from './components/SearchImageForm';
import { Form } from '../../../share/components/Form/Form';
import { IImageStatusType } from '../../../share/types/adhoc';
import { SearchResults } from './components/SearchResults';
import { IAdhocSearch, initialAdhocSearch } from '../../../share/types/adhoc';
import { AdhocService } from '../../../share/services/adhoc';
import { IMatch } from './../../../share/types/match';
import { MatchesService } from './../../../share/services/matches';
import { getLoggedUserEmail } from '../../../share/utils/utils';
import { getAgencyByRole } from '../../../share/utils/auth';
import { IAuthState } from 'src/modules/Auth/store/types';

export interface ISearchByImageState {
  providers: IProvider[];
  submitted: boolean,
  loading: boolean;
  searching: boolean;
  loadingMessage: string;
  providerSelected: boolean,
  imageSelected: boolean,
  imageName: string;
  searchingMessage: any,
  searchInfo: IAdhocSearch,
  imageUploadingStatus: IImageStatusType,
  searchResults: IMatch[],
  loggedUserEmail: string
};

export interface ISearchByImageProps {
  resultsId: string;
  auth?: IAuthState;
  history: any;
};

export class SearchByImage extends React.Component<ISearchByImageProps, ISearchByImageState> {
  public newSearch: IAdhocSearch;
  constructor(props: ISearchByImageProps) {
    super(props);
    const local = sessionStorage.getItem('icmec-providers')
    const data = local ? JSON.parse(local) : [];
    this.state = {
      loading: true,
      searching: false,
      providers: data,
      submitted: false,
      loadingMessage: '',
      providerSelected: false,
      imageSelected: true,
      imageName: '',
      searchInfo: Object.assign({}, initialAdhocSearch),
      imageUploadingStatus: {},
      searchingMessage: '',
      searchResults: [],
      loggedUserEmail: getLoggedUserEmail()
    }
  }
  public componentDidMount() {
    this.loadData();
    if (this.props.resultsId) {
      this.loadResults(this.props.resultsId);
    }
  }
  public loadData() {
    ProvidersService.get().then((results) => {
      const providers = results.filter(result => result.checked && result.type === ProviderType.REKOGNITION);
      sessionStorage.setItem('icmec-providers', JSON.stringify(providers));
      this.setState({ providers, loading: false, searchInfo: Object.assign({}, initialAdhocSearch) });
    }).catch(() => {
      toastr.error('Error', 'There was a problem loading the provider information');
      this.setState({ loading: false });
    })
  }
  public loadResults(resultsId) {
    MatchesService.getAdhoc(resultsId).then(results => {
      this.setState({ searchResults: results });
      this.setValue(`searchInfo.image.url`, results[0].originFace.url)
      this.setValue(`searchInfo.provider`, results[0].matchFace.bucket)
    })
  }
  public setValue = (path: string, value: any) => {
    return new Promise<void>(resolve => {
      const state = { ...this.state };
      _.set(state, path, value)
      this.setState(state, () => resolve());
    })
  }
  public validateFields = () => {
    const { image } = this.state.searchInfo;

    if (_.isEmpty(image.url)) {
      this.setState({ imageSelected: false });
    }
    return image.url;
  }
  public handleSubmit = () => {
    this.setValue('submitted', true);
  }
  public handleInvalidSubmit = () => {
    this.validateFields();
    toastr.error('Search by Image', 'Please fill all the required values');
  }
  public handleValidSubmit = async () => {
    if (this.validateFields()) {
      this.setValue(`searchInfo.email`, this.state.loggedUserEmail)
      this.newSearch = this.state.searchInfo;
      try {
        await this.uploadAndSearch();
        ReactGA.event({
          category: 'Match',
          action: `Searched for a match`,
          label: 'Matches management'
        });
      } catch {
        this.setState({ loading: false, loadingMessage: '' });
        toastr.error('Error', 'There was a problem connecting to the server');
      }
    } else {
      this.handleInvalidSubmit();
    }
  }
  public async uploadAndSearch() {
    this.setState({ loading: true, loadingMessage: 'Uploading image...', searchResults: [], searchingMessage: '' });
    const toSearch = await this.uploadAdhocSearchImage();
    if (toSearch) {
      this.setImageState("SUCCESS")
      this.setState({ loading: false, loadingMessage: '' });
      this.searchProcess(toSearch);
    } else {
      this.setImageState("FAILED")
      this.setState({ loading: false, loadingMessage: '' });
      toastr.error('There was a problem', 'Search image failed to upload, try again')
    }
  }
  public async searchProcess(toSearch: any) {
    this.setState({ searching: true });
    const searchResult = await this.searchImage(toSearch);
    if (searchResult) {
      if (searchResult.success && !searchResult.matches) {
        this.setState({ searching: false, searchingMessage: `The search process could take some time, once the results are ready, an email is going to be sent to: ${this.state.loggedUserEmail}.` });
      } else {
        if (searchResult.matches && searchResult.matches.length > 0) {
          this.setState({ searching: false, searchingMessage: 'Matches found...', searchResults: searchResult.matches });
        } else {
          this.setState({ searching: false, searchingMessage: 'No matches found.' });
        }
      }
    } else {
      this.setState({ searching: false, searchingMessage: 'No matches found.' });
    }
  }

  public async uploadAdhocSearchImage() {
    this.setState({ loadingMessage: 'Uploading and Procesing image to search' })
    return this.uploadSearchImage();
  }
  public async searchImage(searchBy) {
    const { provider, email } = this.newSearch;
    const agencyCode = getAgencyByRole(this.props.auth!)
    const searchIdClean = searchBy.image.replace(/\/|\./g, ' ').split(' ')[1];
    return Promise.resolve()
      .then(() => AdhocService.searchByImage({ key: searchBy.image, searchId: searchIdClean, provider, email, agencyCode, photoId: searchBy.photoId }))
      .then((response) => response)
  }
  public async uploadSearchImage() {
    const { searchId, image, provider } = this.newSearch;
    let result = null;
    if (image.url && image.url.length > 0) {
      await Promise.resolve()
        .then(() => this.setImageState("UPLOADING"))
        .then(() => AdhocService.uploadImage({ searchId, base64: image.url, provider }))
        .then((response) => result = response)
    }
    return result;
  }
  public setImageState(imageState: 'UPLOADING' | 'SUCCESS' | 'FAILED') {
    return this.setValue(`imageUploadingStatus[image]`, { status: imageState })
  }
  public onClearSearch = () => {
    this.setState(state => ({
      searchingMessage: "",
      searchInfo: { ...state.searchInfo, image: { url: '' } },
      imageName: '',
      imageUploadingStatus: {}
    }));
  }
  public render() {
    const {
      searchInfo, loading, loadingMessage, providers, imageSelected, imageName, imageUploadingStatus, searching, searchResults, searchingMessage
    } = this.state;
    return (
      <div className="search-section white-background">
        <h1>Manual Search</h1>
        {providers.length > 0 ?
          <Form
            onInvalidSubmit={this.handleInvalidSubmit}
            onValidSubmit={this.handleValidSubmit}
            onSubmit={this.handleSubmit}
          >
            <SearchImageForm
              setValue={this.setValue}
              searchInfo={searchInfo}
              loading={loading}
              providers={providers}
              imageName={imageName}
              onClearSearch={this.onClearSearch}
              imageSelected={imageSelected}
              imageUploadingStatus={imageUploadingStatus}
            />
            {
              this.state.loading || this.state.searching ?
                <>
                  <Label id="loadingText" className="text-center saving-text">{loadingMessage}</Label>
                  <PulseLoader size={15} color={'#db8733'} loading={this.state.loading} />
                </> :
                <Button type="submit" id="searchBTN" className="btn btn-outline btn-green-fill">
                  Submit
                </Button>
            }
          </Form> : <div><h1>No providers assigned for this agency</h1></div>}
        <div className="results-section">
          <SearchResults
            searchResults={searchResults}
            searchingMessage={searchingMessage}
            searching={searching}
            provider={searchInfo.provider}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxState) => ({
  auth: state.auth
});

export default connect(mapStateToProps, null)(SearchByImage);

